import { useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { Dialog } from "@mui/material";
import Tooltip from "../../components/shared/Tooltip";

import { Box, BoxContainer, HStack, VStack } from "../../components/utils";
import { globalFilter, sort } from "../../utils/arrays";
import BasicMenu from "../../components/shared/Menu";
import Pagination from "../../components/shared/Pagination";
import { Action, CTADropdown } from "../../components/shared/Dropdown";
import AlertDialog from "../../components/shared/AlertDialog";
import { SpocDetailModel, SpocType } from "../../types/OnBoardDataModel";
import AddEditSpoc from "../../modals/AddEditSpoc";
import { PrimaryCTAButton, ButtonSize } from "../quickRound/CTAButtonComponents";
import { Headings } from "../../components/Headings";
import EmptyTableIllustration from "../../shared/EmptyTableIllustration";
import { useDeleteSpocDetails, useGetSpocDetails } from "../../queries/spocDetails";

export default function SpocTable() {
  const { data: spocDetails } = useGetSpocDetails();
  const { mutate: deleteSpocDetails } = useDeleteSpocDetails();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(10);

  const [activeSpocType, setActiveSpocType] = useState<SpocType>(SpocType.SPOC);

  const [globalFilterText, setGlobalFilterText] = useState("");
  const [sortField, setSortField] = useState<{
    field: keyof SpocDetailModel | "" | undefined;
    ascending: boolean;
  }>({ field: "name", ascending: true });
  const [dialog, setDialog] = useState<{
    message?: string;
    open: boolean;
    spoc?: SpocDetailModel;
    mode?: "Edit" | "Add" | "Delete";
  }>({
    open: false,
    mode: "Add",
  });

  const spocList: SpocDetailModel[] = useMemo(() => {
    const typeFiltered = (spocDetails || []).filter(
      (spoc) => spoc.type === activeSpocType
    );
    const filterResult = globalFilter(typeFiltered, globalFilterText, [
      "name",
      "email",
      "phone",
    ]);
    return sort(filterResult, sortField?.field, sortField?.ascending);
  }, [spocDetails, activeSpocType, globalFilterText, sortField]);

  const currentTableData = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * pageSize;
    const lastPageIndex = firstPageIndex + pageSize;
    return spocList.slice(firstPageIndex, lastPageIndex);
  }, [spocList, currentPage, pageSize]);

  function handleAction(action: Action, spoc?: SpocDetailModel) {
    if (action.name === "Edit" && !action.disabled) {
      setDialog({
        open: true,
        mode: "Edit",
        spoc,
        message: "",
      });
    } else if (action.name === "Add" && !action.disabled) {
      setDialog({
        open: true,
        mode: "Add",
        spoc: { type: activeSpocType, name: "", email: "", phone: "" },
        message: "",
      });
    } else if (action.name === "Delete" && !action.disabled) {
      setDialog({
        open: true,
        mode: "Delete",
        spoc,
        message: "Do you want to delete this SPOC Details?",
      });
    }
  }

  function deleteParticularSpoc(spoc?: SpocDetailModel) {
    if (!spoc?.email) return;
    deleteSpocDetails({ email: spoc.email });
    setDialog({ open: false });
  }

  const spocTypeTabs: { type: SpocType; label: string }[] = [
    { type: SpocType.SPOC, label: "SPOC Details" },
    { type: SpocType.KMP, label: "KMP Details" },
  ];

  return (
    <BoxContainer>
      <HStack className="mb-4 border-b">
        {spocTypeTabs.map((tab) => (
          <button
            key={tab.type}
            type="button"
            onClick={() => {
              setActiveSpocType(tab.type);
              setCurrentPage(1);
            }}
            className={`px-4 py-2 text-sm font-medium transition-colors -mb-px border-b-2 ${
              activeSpocType === tab.type
                ? "text-orange-600 border-orange-600"
                : "text-gray-light border-transparent hover:text-gray-dark"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </HStack>
      <HStack aria-label="toolbar" className="justify-between mb-8 sm:flex-row flex-col">
        <Headings
          text={spocTypeTabs.find((tab) => tab.type === activeSpocType)?.label || "SPOC Details"}
          subText={`${spocList?.length || 0} ${activeSpocType === SpocType.KMP ? "KMPs" : "SPOCs"}`}
        />
        <HStack className="h-8 min-w-min sm:mt-0 gap-4 mt-2">
          <HStack className="items-center p-2 rounded w-full bg-transparent text-slate-dark border border-inputBorderFocus">
            <Icon icon="fe:search" width="24" className="mr-2" />
            <input
              type="text"
              className="w-full font-medium border-none outline-none focus:ring-0 text-xs2 bg-inherit"
              placeholder="Search"
              value={globalFilterText}
              onChange={(e) => {
                setGlobalFilterText(e.target.value);
              }}
            ></input>
          </HStack>

          {/* <SpocTemplateActions /> */}
          <PrimaryCTAButton
            className="min-w-max"
            event-name="Add Spoc"
            buttonSize={ButtonSize.SMALL}
            onClick={() => handleAction({ name: "Add", disabled: false })}
          >
            Add
          </PrimaryCTAButton>
        </HStack>
      </HStack>
      <Dialog open={dialog.open} maxWidth="md">
        {dialog.mode === "Delete" ? (
          <AlertDialog
            onClose={() => setDialog({ open: false })}
            open={dialog.open}
            message={dialog.message}
            onPrimaryAction={() => deleteParticularSpoc(dialog.spoc)}
            onSecondaryAction={() => setDialog({ open: false })}
          />
        ) : (
          <AddEditSpoc
            spoc={dialog.spoc}
            onSecondaryAction={() => setDialog({ open: false })}
            mode={dialog.mode}
          />
        )}
      </Dialog>
      <VStack className="w-full gap-8">
        <table className="w-full table-space">
          <thead className="text-xs font-medium text-gray-light">
            <tr>
              <td
                className="py-3 hover:cursor-pointer"
                onClick={() =>
                  setSortField({
                    field: "name",
                    ascending: !sortField?.ascending,
                  })
                }
              >
                {activeSpocType === SpocType.KMP ? "KMP Name" : "SPOC Name"}
              </td>
              <td
                className="py-3 hover:cursor-pointer"
                onClick={() =>
                  setSortField({
                    field: "email",
                    ascending: !sortField?.ascending,
                  })
                }
              >
                Email Id
              </td>
              <td
                className="py-3 hover:cursor-pointer"
                onClick={() =>
                  setSortField({
                    field: "phone",
                    ascending: !sortField?.ascending,
                  })
                }
              >
                Phone Number
              </td>
              <td className="py-3"></td>
            </tr>
          </thead>
          <tbody>
            {currentTableData?.map((spoc, index) => (
              <tr key={spoc.email || index} className="border-t border-dashed">
                <td className="py-4 align-top">
                  <HStack className="w-32">
                    <Box>
                      <p className={` text-xs font-medium text-gray-dark `}>{spoc.name}</p>
                    </Box>
                  </HStack>
                </td>
                <td className="py-4 align-top">
                  <Box>
                    <HStack>
                      <p className={` w-32 text-xs font-medium text-gray-dark`}>{spoc.email}</p>
                    </HStack>
                  </Box>
                </td>
                <td className="py-4 align-top">
                  <Box>
                    <p className={` text-xs font-normal text-gray-dark`}>{spoc.phone}</p>
                  </Box>
                </td>
                <td className="px-2 py-4 align-top">
                  <CTADropdown
                    dropdownClassName="bottom-auto"
                    actions={[
                      {
                        name: "Edit",
                        disabled: false,
                      },
                      {
                        name: "Delete",
                        disabled: false,
                      },
                    ]}
                    onAction={(action) => handleAction(action, spoc)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {spocList?.length <= 0 && (
          <div className="text-center text-black">
            <EmptyTableIllustration
              text={
                activeSpocType === SpocType.KMP
                  ? "No KMP Data Found. Please Add the KMP details"
                  : "No SPOC Data Found. Please Add the SPOC details"
              }
            />
          </div>
        )}
        <Box className="flex justify-between mt-8 cursor-pointer">
          <BasicMenu
            defaultValue={pageSize}
            options={[5, 10, 20, 50, 100]}
            onOptionChange={(value) => {
              setPageSize(value);
              setCurrentPage(1);
            }}
          />
          <Pagination
            currentPage={currentPage}
            totalCount={spocList?.length}
            pageSize={pageSize}
            onPageChange={(page: number) => setCurrentPage(page)}
          />
        </Box>
      </VStack>
    </BoxContainer>
  );
}
